import React, { useRef, useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { Formik, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import ArrayMultiselectFieldWithChipsFormik from '~/components/core/ArrayMultiselectFieldWithChipsFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import CancelButton from '~/components/core/Buttons/CancelButton';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import LobMultiSelectWithChipsFormik, {
  isAllLobsSelected,
} from '~/components/core/Formik/LobMultiSelectWithChipsFormik';
import SubOrgMultiSelectWithChipsFormik, {
  isAllSubOrganizationsSelected,
} from '~/components/core/Formik/SubOrgMultiSelectWithChipsFormik';
import Heading from '~/components/core/TextComponents/Heading';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { FundAccountRuleCreateParams, FundAccountRuleInfo } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import FundAccountConditionsQueryBuilderFormik from './QueryBuilder/FundAccountConditionsQueryBuilderFormik';
import type { FundAccountPaymentMethod } from './paymentMethods';
import { FUND_ACCOUNT_PAYMENT_METHOD_OPTIONS } from './paymentMethods';

/** The "every method" sentinel for the payment-method multiselect. `ArrayMultiselectFieldWithChipsFormik`
 *  stores its id in the same array as the real picks, exactly as `SubOrgMultiSelectWithChipsFormik`
 *  does with its numeric all-id, and its `useAllOptionOnMultiSelect` hook then makes All mutually
 *  exclusive with them. `id` and `value` must match: the field is given `allOption.id` while the
 *  hook compares it against the list's *values*. */
const ALL_PAYMENT_METHODS_OPTION = { id: '__all__', label: 'All', value: '__all__' };

const isAllPaymentMethodsSelected = (paymentMethods: string[]): boolean =>
  paymentMethods.includes(ALL_PAYMENT_METHODS_OPTION.id);

export const FIELD_IDS = {
  ID: 'id',
  NAME: 'name',
  ACCOUNT_NUMBER: 'account_number',
  SUB_ORGANIZATION_IDS: 'sub_organization_ids',
  LOBS: 'lobs',
  ALL_LOBS: 'is_all_lobs',
  PAYMENT_METHODS: 'payment_methods',
  IS_ENABLED: 'is_enabled',
  CONDITIONS_JSON: 'conditions_json',
} as const;

interface FormValues {
  id: number | null;
  name: string;
  account_number: string;
  sub_organization_ids: number[];
  /** Holds LOB keys, but `LobMultiSelectWithChipsFormik` also stores its numeric All sentinel
   *  (`ALL_ID = 0`) in this field, so the element type is not `string`. Use `isAllLobsSelected`
   *  to test for it rather than comparing against a literal. */
  lobs: (string | number)[];
  is_all_lobs: boolean;
  /** Carries the All sentinel alongside the real method keys, hence `string[]`. */
  payment_methods: string[];
  is_enabled: boolean;
  /** Opaque RQB JSON tree written by the shared conditions builder. */
  conditions_json?: RuleGroupType | null;
}

const createValidationSchema = (subOrganizationEnabled: boolean) =>
  Yup.object().shape({
    [FIELD_IDS.NAME]: Yup.string().required('Name is required'),
    // `.trim()` mirrors the BE, which rejects a whitespace-only value — the account number is the
    // rule's whole output, so a blank one is a rule that resolves to nothing while reading as
    // configured. A bare `.required()` would let it through and come back as a 400 the user cannot
    // read off the form.
    [FIELD_IDS.ACCOUNT_NUMBER]: Yup.string().trim().required('Account number is required'),
    // The BE rejects a rule that resolves to no sub-organization or no LOB, so the dialog must not
    // be able to produce that state. A flat organization has no rows to pick, hence no rule here.
    [FIELD_IDS.SUB_ORGANIZATION_IDS]: subOrganizationEnabled
      ? Yup.array().min(1, 'At least one sub organization must be selected')
      : Yup.array(),
    [FIELD_IDS.LOBS]: Yup.array().when(FIELD_IDS.ALL_LOBS, {
      is: false,
      then: (schema) => schema.min(1, 'At least one LOB must be selected'),
      otherwise: (schema) => schema,
    }),
    // `is_all=false` with no values is rejected by the engine, which refuses a rule scoped to
    // nothing for every dimension alike.
    //
    // Membership is checked as well as count, because the read side is deliberately permissive
    // (`values: string[]`) while the write side is a `Literal`: a stored rule can name a method the
    // metadata no longer carries, or one scoped to a portal-routed method by another writer, and
    // `ArrayMultiselectFieldWithChipsFormik` keeps a selected value that matches no option. Without
    // this, opening such a rule and pressing Update posts the stale value and returns a 400 the
    // admin cannot read off any field. The allowed set is the one already derived in
    // `paymentMethods.ts` — not a second hand-written list.
    [FIELD_IDS.PAYMENT_METHODS]: Yup.array()
      .min(1, 'At least one payment method must be selected')
      .of(
        Yup.string().oneOf(
          [ALL_PAYMENT_METHODS_OPTION.id, ...FUND_ACCOUNT_PAYMENT_METHOD_OPTIONS.map((option) => option.value)],
          'This rule is scoped to a payment method that is no longer supported — remove it to save.'
        )
      ),
    // No conditions_json rule: an empty tree is valid and means the rule matches every payment in
    // scope, the same semantic as the sibling engines.
  });

/** A scope's identity, independent of which representation the fields currently hold it in.
 *  Reads only its argument, so it lives at module scope rather than being rebuilt per render. */
const scopeSignature = (vals: FormValues): string => {
  const lobsPart =
    vals.is_all_lobs || isAllLobsSelected(vals.lobs) ? 'all' : [...vals.lobs].map(String).sort().join(',');
  // No `subOrganizationEnabled` special-case: a flat organization never writes this field, so
  // including it unconditionally costs nothing and keeps the sub-org dimension load-bearing.
  const subOrgPart = isAllSubOrganizationsSelected(vals.sub_organization_ids)
    ? 'all'
    : [...vals.sub_organization_ids].sort((a, b) => a - b).join(',');
  return `${lobsPart}|${subOrgPart}`;
};

interface AddOrEditFundAccountRuleDialogProps {
  onSubmit: (values: FundAccountRuleCreateParams) => Promise<void>;
  onCancel: () => void;
  rule?: FundAccountRuleInfo | null;
}

const AddOrEditFundAccountRuleDialog: React.FC<AddOrEditFundAccountRuleDialogProps> = ({
  onSubmit,
  onCancel,
  rule,
}) => {
  const { subOrganizationEnabled } = useOrganization() as { subOrganizationEnabled: boolean };

  return (
    <Formik<FormValues>
      initialValues={{
        [FIELD_IDS.ID]: rule?.id ?? null,
        [FIELD_IDS.NAME]: rule?.name ?? '',
        [FIELD_IDS.ACCOUNT_NUMBER]: rule?.account_number ?? '',
        [FIELD_IDS.SUB_ORGANIZATION_IDS]: !subOrganizationEnabled
          ? []
          : rule?.scope?.is_all_sub_orgs
            ? [SubOrgMultiSelectWithChipsFormik.ALL_OPTION.id]
            : (rule?.scope?.sub_organization_ids ?? []),
        [FIELD_IDS.LOBS]: rule?.scope?.lobs ?? [],
        [FIELD_IDS.ALL_LOBS]: rule?.scope?.is_all_lobs ?? false,
        [FIELD_IDS.PAYMENT_METHODS]: rule?.payment_methods?.is_all
          ? [ALL_PAYMENT_METHODS_OPTION.id]
          : (rule?.payment_methods?.values ?? []),
        [FIELD_IDS.IS_ENABLED]: rule?.is_enabled ?? true,
        [FIELD_IDS.CONDITIONS_JSON]: (rule?.conditions as RuleGroupType | null) ?? null,
      }}
      validationSchema={createValidationSchema(subOrganizationEnabled)}
      onSubmit={async (values) => {
        try {
          // A flat organization has no sub-organization rows at all, so "every sub-organization" is
          // the only resolvable scope — the BE's validator rejects an empty id list.
          const isAllSubOrgs = subOrganizationEnabled
            ? isAllSubOrganizationsSelected(values.sub_organization_ids)
            : true;
          const isAllPaymentMethods = isAllPaymentMethodsSelected(values.payment_methods);
          // Either representation counts as "every LOB": the flag the server stores, or the
          // sentinel the field keeps in `lobs`. Reading both, and filtering the sentinel out of
          // the values list, means no transient combination of the two can post `lobs: [0]`
          // against a `list[str]`.
          const isAllLobs = values.is_all_lobs || isAllLobsSelected(values.lobs);

          const submitValues: FundAccountRuleCreateParams = {
            name: values.name,
            account_number: values.account_number.trim(),
            scope: {
              is_all_sub_orgs: isAllSubOrgs,
              sub_organization_ids: isAllSubOrgs ? [] : values.sub_organization_ids,
              is_all_lobs: isAllLobs,
              lobs: isAllLobs ? [] : values.lobs.filter((lob): lob is string => typeof lob === 'string'),
            },
            // One canonical representation of "every method": the sentinel is a UI value the write
            // params do not accept, so it never travels — `is_all` carries the same fact.
            payment_methods: {
              is_all: isAllPaymentMethods,
              values: isAllPaymentMethods ? [] : (values.payment_methods as FundAccountPaymentMethod[]),
            },
            conditions: values.conditions_json ?? null,
            is_enabled: values.is_enabled,
          };
          await onSubmit(submitValues);
          onCancel();
        } catch {
          // Swallowed rather than rethrown, and with no `setSubmitting(false)`: the api layer
          // already surfaced the failure via `reportAxiosError`, Formik clears the flag itself once
          // the returned promise settles, and letting the rejection escape makes Formik's own
          // `handleSubmit` log "An unhandled error was caught from submitForm()" over the toast.
        }
      }}
    >
      <AddOrEditFundAccountRuleDialogInner onCancel={onCancel} />
    </Formik>
  );
};

interface AddOrEditFundAccountRuleDialogInnerProps {
  onCancel: () => void;
}

const AddOrEditFundAccountRuleDialogInner: React.FC<AddOrEditFundAccountRuleDialogInnerProps> = ({ onCancel }) => {
  // `operational_details` builds the field as `sub_organizations_enabled and [...]`, so an
  // organization without sub-organizations gets the boolean `false` rather than an empty list.
  // `?? []` does not catch that (`??` only replaces null/undefined), so normalise here — this is
  // the single read the array consumer below is fed from.
  const { subOrganizationEnabled, subOrganizations: rawSubOrganizations } = useOrganization() as {
    subOrganizationEnabled: boolean;
    subOrganizations: { id: number; name?: string }[] | false;
  };
  const subOrganizations = Array.isArray(rawSubOrganizations) ? rawSubOrganizations : [];
  const { isSubmitting, handleSubmit, values, setFieldValue } = useFormikContext<FormValues>();
  const [isLoadingConditions, setIsLoadingConditions] = useState(false);
  const { classes } = useStyles();

  const isDisabled = isSubmitting || isLoadingConditions;
  const isEditing = values.id !== null;
  const subOrganizationIds = values.sub_organization_ids;
  // "All (inc. future lobs)" is stored as the sentinel id 0, not as a LOB key, so it must not reach
  // the catalog as a LOB named "0". Read off the forwarded value rather than `is_all_lobs`: that flag
  // is set by a change listener one render later, and the intervening render would fire the fetch.
  const catalogLobs = isAllLobsSelected(values.lobs)
    ? []
    : values.lobs.filter((lob): lob is string => typeof lob === 'string');

  // Clear the conditions tree whenever a scope dimension changes: the catalog refetches on these
  // keys (LOB and sub-org narrow which providers contribute), so a tree authored under the old
  // scope must not silently carry over.
  //
  // Keyed on a *normalised* signature rather than on the raw fields, because not every write to
  // them is a user edit: `LobMultiSelectWithChipsFormik` rewrites both LOB fields during mount to
  // move an all-LOBs rule from the server's representation (`is_all_lobs` with an empty `lobs`)
  // into its own (the All sentinel inside `lobs`). Those writes describe the same scope, so they
  // must compare equal here — otherwise merely opening such a rule nulls its conditions and the
  // next Update posts that null as an explicit clear.
  // Lazily initialised: `useRef(x)` evaluates `x` on every render and keeps only the first, so
  // seeding it inline would recompute the signature for nothing on each pass.
  const lastScopeRef = useRef<string | null>(null);
  if (lastScopeRef.current === null) {
    lastScopeRef.current = scopeSignature(values);
  }

  useFormikChangeListener({
    listenForKeys: [FIELD_IDS.LOBS, FIELD_IDS.ALL_LOBS, FIELD_IDS.SUB_ORGANIZATION_IDS],
    onChange: (newValues: Record<string, unknown>) => {
      const signature = scopeSignature(newValues as unknown as FormValues);
      if (signature === lastScopeRef.current) {
        return;
      }
      lastScopeRef.current = signature;
      setFieldValue(FIELD_IDS.CONDITIONS_JSON, null);
    },
    runOnFirstRender: false,
  });

  return (
    <CardDialog
      isDialog
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      title={`${isEditing ? 'Edit' : 'Add'} Fund Account Rule`}
    >
      <AlertBanner
        className="mb-4"
        alertType={AlertBanner.ALERT_TYPES.INFO}
        note="Editing the sub organizations or lines of business will clear all conditions."
        title="Please Note"
        withIcon
      />
      <TextFieldFormik
        id={FIELD_IDS.NAME}
        label="Name"
        className={cn(classes.textField, 'mb-20')}
        fullWidth
        disabled={isDisabled}
      />
      <div className="rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          Context
        </Heading>
        {subOrganizationEnabled ? (
          <div className="mb-12">
            <SubOrgMultiSelectWithChipsFormik
              id={FIELD_IDS.SUB_ORGANIZATION_IDS}
              label="Sub Organizations"
              showOnly={isSubmitting}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        <div className="mb-12">
          <LobMultiSelectWithChipsFormik
            lobsFieldId={FIELD_IDS.LOBS}
            allLobsFieldId={FIELD_IDS.ALL_LOBS}
            {...(subOrganizationEnabled
              ? {
                  subOrganizationIds: isAllSubOrganizationsSelected(subOrganizationIds)
                    ? subOrganizations.map((subOrg) => subOrg.id)
                    : subOrganizationIds,
                  subOrgFieldId: FIELD_IDS.SUB_ORGANIZATION_IDS,
                  shouldResetOnSubOrgChange: true,
                  disabled: isEmpty(subOrganizationIds),
                }
              : {})}
          />
        </div>
        {/* The options array is mapped inline on purpose: `ArrayMultiselectFieldWithChipsFormik`
            does `options.unshift(allOption)` during render, mutating the array it is handed.
            Hoisting this to a module constant would permanently prepend "All" to that shared
            constant on first render (the component's `!options.includes(allOption)` guard keeps it
            to one, so the damage is a leaked mutation, not a growing list). A fresh array per
            render keeps the constant clean. */}
        <ArrayMultiselectFieldWithChipsFormik
          id={FIELD_IDS.PAYMENT_METHODS}
          label="Payment Methods"
          options={FUND_ACCOUNT_PAYMENT_METHOD_OPTIONS.map((option) => ({
            id: option.value,
            label: option.label,
            value: option.value,
          }))}
          allOption={ALL_PAYMENT_METHODS_OPTION}
          showOnly={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
      <TextFieldFormik
        id={FIELD_IDS.ACCOUNT_NUMBER}
        label="Account Number"
        className={cn(classes.textField, 'mt-20 w-1/2')}
        fullWidth
        disabled={isDisabled}
      />
      <div className="px-4 py-20">
        <FundAccountConditionsQueryBuilderFormik
          fieldId={FIELD_IDS.CONDITIONS_JSON}
          // Every picked LOB: the catalog is the cross-LOB intersection, which is also what
          // save-time validation checks the rule against. All-LOBs rules render no QB until the
          // admin narrows to concrete LOBs.
          lobs={catalogLobs}
          isAllSubOrgs={subOrganizationEnabled ? isAllSubOrganizationsSelected(subOrganizationIds) : true}
          subOrgIds={subOrganizationEnabled ? subOrganizationIds : []}
          onLoadingChange={setIsLoadingConditions}
        />
      </div>
      <ButtonsContainer>
        <CancelButton onClick={onCancel} disabled={isSubmitting} />
        <Button color="primary" onClick={() => handleSubmit()} disabled={isDisabled}>
          {isEditing ? 'Update' : 'Save'}
        </Button>
      </ButtonsContainer>
    </CardDialog>
  );
};

export default AddOrEditFundAccountRuleDialog;
// The inner component is exported for testing the Formik wiring (the scope-change reset of the
// conditions tree). Production code should use the default export.
export { AddOrEditFundAccountRuleDialogInner };
